<script lang="ts">
  import { tick } from 'svelte';
  import ArrowLeftIcon from 'phosphor-svelte/lib/ArrowLeftIcon';

  import type { ImagePackView } from '#src/generated/protocol';

  import { useCoreClient } from '#lib/core/context.js';
  import { invalidatePacks } from '#lib/emoji/load-packs.js';
  import { useRoomList } from '#lib/rooms/room-list.svelte.js';
  import { i18n } from '#lib/i18n.js';
  import MediaImage from '#lib/ui/MediaImage.svelte';
  import Alert from '#lib/ui/primitives/Alert.svelte';
  import AppPageShell from '#lib/ui/primitives/AppPageShell.svelte';
  import Button from '#lib/ui/primitives/Button.svelte';
  import SettingsRow from '#lib/ui/primitives/SettingsRow.svelte';
  import SettingsSection from '#lib/ui/primitives/SettingsSection.svelte';
  import Spinner from '#lib/ui/primitives/Spinner.svelte';
  import Switch from '#lib/ui/primitives/Switch.svelte';

  import { findCategory, SETTINGS_EMOTES_SECTION } from '#lib/settings/registry.js';
  import SettingsCategorySections from '#lib/features/settings/SettingsCategorySections.svelte';

  import ImagePackEditor from './ImagePackEditor.svelte';
  import { packDraft } from './pack-content.js';
  import { exportPacks } from './pack-transfer.js';
  import PersonalPackSettings from './PersonalPackSettings.svelte';
  import { packAddressEqual, packAddressKey, type PackAddress } from './pack-address.js';

  import '#lib/ui/primitives/settings-row.css';

  const core = useCoreClient();
  const roomList = useRoomList();
  const category = findCategory(SETTINGS_EMOTES_SECTION);

  let packs = $state.raw<ImagePackView[]>([]);
  let chosen = $state.raw<PackAddress[]>([]);
  let loading = $state(true);
  let busy = $state(false);
  let failed = $state(false);
  let viewing = $state<string | null>(null);
  let alive = true;
  let root = $state<HTMLElement | null>(null);
  let listScroll = 0;

  let roomPacks = $derived(packs.filter((pack) => pack.origin === 'room' && pack.room_id !== null));
  let viewingPack = $derived(
    roomPacks.find((pack) => pack.room_id !== null && packAddressKey(address(pack)) === viewing) ??
      null
  );

  function address(pack: ImagePackView): PackAddress {
    return { room_id: pack.room_id ?? '', state_key: pack.id };
  }

  function scroller(): HTMLElement | null {
    const found = root?.closest('.settings-scroll');
    return found instanceof HTMLElement ? found : null;
  }

  async function view(pack: ImagePackView): Promise<void> {
    listScroll = scroller()?.scrollTop ?? 0;
    viewing = packAddressKey(address(pack));
    await tick();
    const target = scroller();
    if (target) target.scrollTop = 0;
  }

  async function backToList(): Promise<void> {
    viewing = null;
    await tick();
    const target = scroller();
    if (target) target.scrollTop = listScroll;
  }

  function isChosen(pack: ImagePackView): boolean {
    return chosen.some((entry) => packAddressEqual(entry, address(pack)));
  }

  let byRoom = $derived(
    Object.entries(
      roomPacks.reduce<Record<string, ImagePackView[]>>((grouped, pack) => {
        (grouped[pack.room_id ?? ''] ??= []).push(pack);
        return grouped;
      }, {})
    ).sort(([left], [right]) => roomList.labelFor(left).localeCompare(roomList.labelFor(right)))
  );

  $effect(() => {
    void load().finally(() => {
      if (alive) loading = false;
    });

    return () => {
      alive = false;
    };
  });

  async function load(): Promise<void> {
    try {
      const [loaded, subscribed] = await Promise.all([
        core.rpc.packs.listAll(),
        core.rpc.packs.subscriptions(),
      ]);
      if (!alive) return;
      packs = loaded;
      chosen = subscribed;
    } catch (error) {
      console.warn('[sable emotes] the pack list could not be read', error);
      if (alive) failed = true;
    }
  }

  async function toggle(pack: ImagePackView, wanted: boolean): Promise<void> {
    const target = [address(pack)];

    busy = true;
    failed = false;
    try {
      chosen = wanted
        ? await core.rpc.packs.setSubscriptions({ added: target, removed: [] })
        : await core.rpc.packs.setSubscriptions({ added: [], removed: target });
      invalidatePacks(core.rpc);
    } catch (error) {
      console.warn('[sable emotes] the pack selection could not be saved', error);
      failed = true;
    } finally {
      busy = false;
    }
  }

  async function exportRoom(roomsPacks: ImagePackView[]): Promise<void> {
    if (busy) return;

    busy = true;
    failed = false;
    try {
      if ((await exportPacks(core, roomsPacks.map(packDraft))) === 'failed') failed = true;
    } catch (error) {
      console.warn('[sable emotes] the room packs could not be exported', error);
      failed = true;
    } finally {
      busy = false;
    }
  }

  async function toggleRoom(roomId: string, roomsPacks: ImagePackView[]): Promise<void> {
    const all = roomsPacks.every(isChosen);
    const addresses = roomsPacks.map(address);

    busy = true;
    failed = false;
    try {
      chosen = all
        ? await core.rpc.packs.setSubscriptions({ added: [], removed: addresses })
        : await core.rpc.packs.setSubscriptions({ added: addresses, removed: [] });
      invalidatePacks(core.rpc);
    } catch (error) {
      console.warn('[sable emotes] the pack selection could not be saved', error);
      failed = true;
    } finally {
      busy = false;
    }
    void roomId;
  }
</script>

<AppPageShell title={i18n.t('settings.emotes')} density="compact" class="emote-settings-page">
  <div class="emote-settings" bind:this={root}>
    {#if failed}
      <Alert variant="critical" role="alert">{i18n.t('emotes.saveFailed')}</Alert>
    {/if}

    {#if viewingPack}
      <div class="viewer-header">
        <Button
          size="small"
          onclick={() => {
            void backToList();
          }}
        >
          <ArrowLeftIcon />
          {i18n.t('common.back')}
        </Button>
      </div>
      {#key viewing}
        <ImagePackEditor pack={viewingPack} canEdit={false} />
      {/key}
    {:else}
      {#if category}<SettingsCategorySections {category} />{/if}
      <PersonalPackSettings />

      <SettingsSection
        headingId="emote-room-packs"
        title={i18n.t('emotes.roomPacksTitle')}
        description={i18n.t('emotes.roomPacksHint')}
      >
        {#if loading}
          <p class="status settings-form" role="status"><Spinner small /></p>
        {:else if byRoom.length === 0}
          <p class="status settings-form">{i18n.t('emotes.noRoomPacks')}</p>
        {:else}
          {#each byRoom as [roomId, roomsPacks] (roomId)}
            <div class="room-group">
              <div class="room-heading">
                <span>{roomList.labelFor(roomId)}</span>
                <div class="room-actions">
                  <Button
                    size="small"
                    disabled={busy}
                    onclick={() => {
                      void exportRoom(roomsPacks);
                    }}
                  >
                    {i18n.t('emotes.exportAll')}
                  </Button>
                  <Button
                    size="small"
                    disabled={busy}
                    onclick={() => {
                      void toggleRoom(roomId, roomsPacks);
                    }}
                  >
                    {roomsPacks.every(isChosen)
                      ? i18n.t('emotes.unselectAll')
                      : i18n.t('common.selectAll')}
                  </Button>
                </div>
              </div>
              <ul class="settings-rows">
                {#each roomsPacks as pack (pack.id)}
                  <SettingsRow
                    title={pack.name ?? pack.id}
                    description={i18n.t('emotes.imageCount', { count: pack.images.length })}
                  >
                    {#snippet before()}
                      <MediaImage
                        source={pack.avatar_url ?? pack.images[0]?.url ?? ''}
                        alt=""
                        width={24}
                        height={24}
                        original
                      />
                    {/snippet}
                    <Button
                      size="small"
                      onclick={() => {
                        void view(pack);
                      }}
                    >
                      {i18n.t('emotes.view')}
                    </Button>
                    <Switch
                      label={i18n.t('emotes.useEverywhere', { name: pack.name ?? pack.id })}
                      checked={isChosen(pack)}
                      disabled={busy}
                      onCheckedChange={(checked) => {
                        void toggle(pack, checked);
                      }}
                    />
                  </SettingsRow>
                {/each}
              </ul>
            </div>
          {/each}
        {/if}
      </SettingsSection>
    {/if}
  </div>
</AppPageShell>

<style>
  :global(.app-page-shell.emote-settings-page) {
    max-width: 56rem;
  }

  .emote-settings {
    display: grid;
    gap: var(--space-300);
  }

  .viewer-header {
    background: var(--surface-container);
    display: flex;
    inset-block-start: 0;
    padding-block: var(--space-200);
    position: sticky;
    z-index: 1;
  }

  .room-group {
    display: grid;
    gap: var(--space-100);
  }

  .room-heading {
    align-items: center;
    color: var(--surface-var-on-container);
    display: flex;
    font-size: var(--font-size-small);
    gap: var(--space-200);
    justify-content: space-between;
    padding: var(--space-200) var(--space-400) 0;
  }

  .room-actions {
    display: flex;
    gap: var(--space-200);
  }

  .status {
    color: var(--surface-var-on-container);
    margin: 0;
    text-align: center;
  }
</style>
